import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import bbox from "turf-bbox";
import { WebMercatorViewport } from "@math.gl/web-mercator";
import chunk from "lodash.chunk";
import debounce from "lodash.debounce";
import { getIsDesktop } from "../../hooks";
import Map from "./Map";
import { Decimal } from "decimal.js";
import { useMeasure } from "react-use";

export function useViewport(defaultViewport) {
    const [viewport, setViewport] = useState(
        defaultViewport || {
            latitude: 45.958913,
            longitude: -78.328043,
            zoom: 5,
        },
    );
    return [viewport, setViewport];
}

export const createFitBounds = geojsonObject => ({
    mapSize,
    onViewportChange,
    useTwoColumnLayout,
    useOldLayout = true,
}) => {
    const viewport = {
        latitude: 45.958913,
        longitude: -78.328043,
        zoom: 5,
    };
    const bounds = bbox(geojsonObject);
    const isDesktop = getIsDesktop();
    const isInTwoColumnLayout = useOldLayout ? isDesktop : useTwoColumnLayout;
    try {
        const { zoom, latitude, longitude } = new WebMercatorViewport({
            width: mapSize.width,
            height: mapSize.height,
            zoom: viewport.zoom,
            latitude: viewport.latitude,
            longitude: viewport.longitude,
        }).fitBounds(chunk(bounds, 2), {
            padding: {
                top: isInTwoColumnLayout ? 75 + (useOldLayout ? 0 : 108 / 2) : 40,
                bottom: isInTwoColumnLayout ? 75 + (useOldLayout ? 0 : 108 / 2) : 40,
                left: isInTwoColumnLayout ? 75 : 40,
                right: isInTwoColumnLayout ? 75 : 40,
            },
        });
        const newViewport = {
            zoom,
            latitude,
            longitude,
        };
        if (!useOldLayout) {
            newViewport.manuallyChanged = false;
        }
        onViewportChange(newViewport);
    } catch (e) {
        console.warn(e);
    }
};

function MapContainer(props) {
    const {
        style,
        isTertiaryLayout,
        markerClasses,
        inItinerary,
        isMapOverlayVisible,
        mapOverlayText,
        mapHeight,
        className,
        fitBounds,
        mainFitBounds,
        viewport,
        onViewportChange,
        markers,
        activeMarkerIds,
        activeSecondaryMarkerIds,
        children,
        isAutoFollowEnabled,
        setIsAutoFollowEnabled,
        showAutoFollow,
        isSecondaryLayout,
        mapClassName,
        onMarkerClick,
        onMarkerMouseEnter,
        onMarkerMouseLeave,
        useTwoColumnLayout,
        useOldLayout,
        withMapControls,
        reCenterOnResize,
        setMapboxInstance,
        isDesktop,
        ...restProps
    } = props;
    const mapRef = useRef();
    const [wrapperMapRef, { width: mapComputedWidth, height: mapComputedHeight }] = useMeasure();
    const [mapCenterState, setMapCenterState] = useState(null);
    const mapboxInstance = mapRef?.current?.state?.map;
    const mapSize = useMemo(() => {
        return {
            width: mapComputedWidth,
            height: mapComputedHeight,
        };
    }, [mapComputedWidth, mapComputedHeight]);
    const containerStyle = useMemo(() => {
        return {
            height: mapHeight,
            width: "100%",
        };
    }, [mapHeight]);
    const onManualInteraction = useCallback(() => {
        if (!withMapControls) {
            return;
        }
        onViewportChange({
            ...viewport,
            manuallyChanged: true,
        });
    }, [onViewportChange, viewport, withMapControls]);
    const reCenter = useCallback(() => fitBounds({ mapSize, onViewportChange, useOldLayout, useTwoColumnLayout }), [
        mapSize,
        onViewportChange,
        fitBounds,
        useOldLayout,
        useTwoColumnLayout,
    ]);
    const mainReCenter = useCallback(
        () => mainFitBounds({ mapSize, onViewportChange, useOldLayout, useTwoColumnLayout }),
        [mapSize, onViewportChange, mainFitBounds, useOldLayout, useTwoColumnLayout],
    );
    const zoomIn = useCallback(() => mapboxInstance && mapboxInstance.zoomIn(), [mapboxInstance]);
    const zoomOut = useCallback(() => mapboxInstance && mapboxInstance.zoomOut(), [mapboxInstance]);
    const showReCenter = useMemo(() => {
        if (useTwoColumnLayout) {
            return true;
        }
        if (!mapboxInstance) {
            return true;
        }
        if (!viewport.manuallyChanged) {
            return false;
        }
        const zoom = mapboxInstance.getZoom();
        const { lng: longitude, lat: latitude } = mapboxInstance.getCenter();
        const currentViewport = {
            zoom,
            longitude,
            latitude,
        };
        const isLonEqual =
            new Decimal(currentViewport?.longitude ?? 0).toFixed(7) ===
            new Decimal(mapCenterState?.longitude ?? 0).toFixed(7);
        const isLatEqual =
            new Decimal(currentViewport?.latitude ?? 0).toFixed(7) ===
            new Decimal(mapCenterState?.latitude ?? 0).toFixed(7);
        const isZoomEqual =
            new Decimal(currentViewport?.zoom ?? 0).toFixed(7) === new Decimal(mapCenterState?.zoom ?? 0).toFixed(7);
        if (isLonEqual && isLatEqual && isZoomEqual) {
            return false;
        }
        return true;
        //eslint-disable-next-line
    }, [mapRef, viewport, mapCenterState, useTwoColumnLayout, mapboxInstance]);

    const onMapboxViewportChanges = useMemo(
        () =>
            debounce(mapboxInstance => {
                const zoom = mapboxInstance.getZoom();
                const { lng: longitude, lat: latitude } = mapboxInstance.getCenter();
                onViewportChange({
                    ...viewport,
                    longitude,
                    latitude,
                    zoom,
                });
            }, 200),
        [viewport, onViewportChange],
    );

    //Pass mapboxInstance to parent
    useEffect(() => {
        if (!setMapboxInstance) {
            return;
        }
        setMapboxInstance(mapboxInstance);
    }, [mapboxInstance, setMapboxInstance]);

    //Update mapCenterState
    useEffect(() => {
        if (!mapSize.width || !mapSize.height) {
            return;
        }
        fitBounds({
            mapSize,
            onViewportChange: data => {
                setMapCenterState(data);
            },
            useOldLayout,
            useTwoColumnLayout,
        });
    }, [fitBounds, mapSize, useOldLayout, useTwoColumnLayout]);

    //Recenter on map size change
    useEffect(() => {
        if (!reCenterOnResize || !mapSize.width || !mapSize.height) {
            return;
        }
        reCenter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainFitBounds, mapSize]);

    //Resize map correctly
    useEffect(() => {
        if (!mapboxInstance) {
            return;
        }
        mapboxInstance.resize();
        //eslint-disable-next-line
    }, [mapSize]);

    //Show full view on disabling auto follow
    const _setIsAutoFollowEnabled = useCallback(
        newValue => {
            setIsAutoFollowEnabled(newValue);
            if (!newValue) {
                mainReCenter();
            } else {
                reCenter();
            }
        },
        [mainReCenter, setIsAutoFollowEnabled, reCenter],
    );

    return (
        <Map
            style={style}
            isTertiaryLayout={isTertiaryLayout}
            markerClasses={markerClasses}
            inItinerary={inItinerary}
            isMapOverlayVisible={isMapOverlayVisible}
            mapOverlayText={mapOverlayText}
            ref={mapRef}
            className={className}
            mapClassName={mapClassName}
            viewport={viewport}
            reCenter={reCenter}
            markers={markers}
            activeMarkerIds={activeMarkerIds}
            activeSecondaryMarkerIds={activeSecondaryMarkerIds}
            zoomIn={zoomIn}
            zoomOut={zoomOut}
            containerStyle={containerStyle}
            isAutoFollowEnabled={isAutoFollowEnabled}
            setIsAutoFollowEnabled={_setIsAutoFollowEnabled}
            showAutoFollow={showAutoFollow}
            isSecondaryLayout={isSecondaryLayout}
            onMarkerClick={onMarkerClick}
            onMarkerMouseEnter={onMarkerMouseEnter}
            onMarkerMouseLeave={onMarkerMouseLeave}
            onViewportChange={onViewportChange}
            onMapboxViewportChanges={onMapboxViewportChanges}
            onManualInteraction={onManualInteraction}
            showReCenter={showReCenter}
            withMapControls={withMapControls}
            useTwoColumnLayout={useOldLayout ? isDesktop : useTwoColumnLayout}
            wrapperMapRef={wrapperMapRef}
            mapboxInstance={mapboxInstance}
            {...restProps}
        >
            {children}
        </Map>
    );
}

MapContainer.defaultProps = {
    reCenterOnResize: true,
    useOldLayout: true,
    showAutoFollow: true,
    isAutoFollowEnabled: false,
    markers: [],
    viewport: {
        zoom: 11,
    },
    onViewportChange: () => {},
    fitBounds: () => {},
    mainFitBounds: () => {},
    setIsAutoFollowEnabled: () => {},
    activeMarkerIds: [],
};

MapContainer.propTypes = {
    onViewportChange: PropTypes.func,
    fitBounds: PropTypes.func,
    activeMarkerIds: PropTypes.arrayOf(PropTypes.string),
    viewport: PropTypes.shape({
        longitude: PropTypes.number.isRequired,
        latitude: PropTypes.number.isRequired,
        zoom: PropTypes.number,
    }).isRequired,
    markers: PropTypes.arrayOf(
        PropTypes.shape({
            longitude: PropTypes.number.isRequired,
            latitude: PropTypes.number.isRequired,
        }),
    ),
};

export default MapContainer;
